import { AlertTriangle, Loader2, Send } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { formatPhoneNumber } from '@/components/audio-conversation/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Z_INDEX_MODAL_HIGH,
  Z_INDEX_MODAL_NESTED,
} from '@/components/ui/zIndex';
import {
  type ContextualSmsTemplateResponse,
  type SmsTemplateCategory,
  type SmsTemplateListResponse,
  type SmsTemplateSchema,
  useGlobalSmsTemplatesListQuery,
  useMatterSmsTemplateContextualQuery,
} from '@/services/api/generated/smsTemplatesService';
import {
  type ConversationMessageSchema,
  useTreatmentPreviewSmsMutation,
  useTreatmentSendSmsMutation,
} from '@/services/api/generated/treatmentSmsService';
import { useGetMatterQuery } from '@/services/api/mattersService';

import { ConversationHistory } from './ConversationHistory';
import { TemplateSelector } from './TemplateSelector';

const CATEGORY_OPTIONS: { value: SmsTemplateCategory; label: string }[] = [
  { value: 'Document Collection', label: 'Document Collection' },
  {
    value: 'Initial Treatment Assessment',
    label: 'Initial Treatment Assessment',
  },
  { value: 'Ongoing Treatment Check-In', label: 'Ongoing Treatment Check-In' },
];

interface SmsComposerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: string;
  clientName: string;
  clientPhone?: string;
  taskId?: string;
  initialCategory?: SmsTemplateCategory;
  onSmsSent?: () => void;
}

export const SmsComposerModal = ({
  open,
  onOpenChange,
  matterId,
  clientName,
  clientPhone,
  taskId,
  initialCategory,
  onSmsSent,
}: SmsComposerModalProps) => {
  const [messageMode, setMessageMode] = useState<'template' | 'custom'>(
    'template',
  );
  const [selectedTemplate, setSelectedTemplate] =
    useState<SmsTemplateSchema | null>(null);
  const [customMessage, setCustomMessage] = useState('');
  const [editedTemplateMessage, setEditedTemplateMessage] = useState('');
  const [hasEditedTemplate, setHasEditedTemplate] = useState(false);
  const [recentMessages, setRecentMessages] = useState<
    ConversationMessageSchema[]
  >([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<SmsTemplateCategory | null>(null);
  const [isSending, setIsSending] = useState(false);

  const { data: matterData } = useGetMatterQuery(matterId, { skip: !open });

  const effectiveCategory = selectedCategory ?? initialCategory ?? null;

  // Fetch templates based on selected category (or inferred)
  const { data: templateData, isLoading: isLoadingTemplates } =
    useMatterSmsTemplateContextualQuery(
      {
        matterId,
        categoryOverride: effectiveCategory ?? undefined,
      },
      { skip: !open },
    );

  const senderConfigured = Boolean(matterData?.firm?.sms_sender_number);

  const [previewSms] = useTreatmentPreviewSmsMutation();
  const [sendSms] = useTreatmentSendSmsMutation();

  // Workaround: API may return array (old types) or single object (new backend)
  const contextualData: ContextualSmsTemplateResponse | null | undefined =
    (() => {
      if (!templateData) return null;
      const data = templateData as unknown;
      if (Array.isArray(data)) return data[0] as ContextualSmsTemplateResponse;
      return data as ContextualSmsTemplateResponse;
    })();
  const contextualTemplates = contextualData?.templates ?? [];
  const recommendedTemplate = contextualData?.recommended_template;
  const mergeFieldContext: Record<string, string | null> =
    contextualData?.merge_field_context ?? {};
  const inferredCategory = contextualData?.inferred_category;
  const categoryForGlobalTemplates =
    effectiveCategory ?? inferredCategory ?? undefined;

  const { data: globalTemplatesData } = useGlobalSmsTemplatesListQuery(
    {
      activeOnly: true,
      category: categoryForGlobalTemplates,
    },
    { skip: !open || !categoryForGlobalTemplates },
  );

  const globalTemplates = useMemo(() => {
    if (!globalTemplatesData) return [];
    const data = globalTemplatesData as unknown;
    if (Array.isArray(data)) {
      return (data as SmsTemplateListResponse[]).flatMap(
        (response) => response.templates ?? [],
      );
    }
    return (data as SmsTemplateListResponse).templates ?? [];
  }, [globalTemplatesData]);

  const templates = useMemo(() => {
    if (globalTemplates.length === 0) return contextualTemplates;
    const merged = [...contextualTemplates];
    const existingIds = new Set(
      contextualTemplates.map((template) => template.id),
    );
    globalTemplates.forEach((template) => {
      if (!existingIds.has(template.id)) {
        merged.push(template);
      }
    });
    return merged;
  }, [contextualTemplates, globalTemplates]);

  const previewTemplateId = useMemo(() => {
    return (
      recommendedTemplate?.id ??
      contextualTemplates[0]?.id ??
      globalTemplates[0]?.id ??
      null
    );
  }, [recommendedTemplate?.id, contextualTemplates, globalTemplates]);

  useEffect(() => {
    if (open && initialCategory && !selectedCategory) {
      setSelectedCategory(initialCategory);
    }
  }, [open, initialCategory, selectedCategory]);

  // Set initial category from inferred
  useEffect(() => {
    if (open && inferredCategory && !selectedCategory && !initialCategory) {
      setSelectedCategory(inferredCategory);
    }
  }, [open, inferredCategory, initialCategory, selectedCategory]);

  useEffect(() => {
    if (open && recommendedTemplate && !selectedTemplate) {
      setSelectedTemplate(recommendedTemplate);
      setEditedTemplateMessage('');
      setHasEditedTemplate(false);
    }
  }, [open, recommendedTemplate, selectedTemplate]);

  useEffect(() => {
    if (!open) {
      setSelectedTemplate(null);
      setCustomMessage('');
      setEditedTemplateMessage('');
      setHasEditedTemplate(false);
      setMessageMode('template');
      setRecentMessages([]);
      setShowConfirmDialog(false);
      setSelectedCategory(null);
      setIsSending(false);
    }
  }, [open]);

  // Fetch conversation history when modal opens
  useEffect(() => {
    if (open && matterId) {
      if (!previewTemplateId) return;

      previewSms({
        previewTreatmentSmsRequest: {
          matter_id: matterId,
          template_id: previewTemplateId,
        },
      })
        .unwrap()
        .then((result) => {
          setRecentMessages([...(result.recent_messages ?? [])].reverse());
        })
        .catch(() => {
          // Conversation history fetch failed - not critical
        });
    }
  }, [open, matterId, previewTemplateId, previewSms]);

  // Render the message with merge field substitution
  const renderMessageWithMergeFields = useCallback(
    (message: string) => {
      if (!message) return '';
      let rendered = message;
      Object.entries(mergeFieldContext).forEach(([key, value]) => {
        if (value) {
          rendered = rendered.replace(
            new RegExp(`\\{\\{${key}\\}\\}`, 'g'),
            value,
          );
        }
      });
      return rendered;
    },
    [mergeFieldContext],
  );

  // Get the current message to display/send
  const currentMessage = useMemo(() => {
    if (messageMode === 'custom') {
      return customMessage;
    }
    // If user has edited the template message, use that (even if empty)
    if (hasEditedTemplate) {
      return editedTemplateMessage;
    }
    return selectedTemplate?.content ?? '';
  }, [
    messageMode,
    customMessage,
    hasEditedTemplate,
    editedTemplateMessage,
    selectedTemplate,
  ]);

  const renderedMessage = useMemo(() => {
    return renderMessageWithMergeFields(currentMessage);
  }, [currentMessage, renderMessageWithMergeFields]);

  const handleTemplateSelect = useCallback(
    (template: SmsTemplateSchema | null) => {
      setSelectedTemplate(template);
      setEditedTemplateMessage('');
      setHasEditedTemplate(false);
    },
    [],
  );

  const handleCategoryChange = useCallback((category: SmsTemplateCategory) => {
    setSelectedCategory(category);
    setSelectedTemplate(null);
    setEditedTemplateMessage('');
    setHasEditedTemplate(false);
  }, []);

  const handleMessageEdit = useCallback(
    (value: string) => {
      if (messageMode === 'custom') {
        setCustomMessage(value);
      } else {
        setEditedTemplateMessage(value);
        setHasEditedTemplate(true);
      }
    },
    [messageMode],
  );

  const handleSendClick = useCallback(() => {
    if (!currentMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    setShowConfirmDialog(true);
  }, [currentMessage]);

  const handleConfirmSend = useCallback(async () => {
    setShowConfirmDialog(false);
    setIsSending(true);

    try {
      // If user edited the template, send as custom message
      const isCustomOrEdited = messageMode === 'custom' || hasEditedTemplate;

      await sendSms({
        sendTreatmentSmsRequest: {
          matter_id: matterId,
          template_id:
            !isCustomOrEdited && selectedTemplate?.id
              ? selectedTemplate.id
              : undefined,
          message_text: isCustomOrEdited ? renderedMessage : undefined,
          task_id: taskId,
        },
      }).unwrap();

      toast.success(`SMS sent to ${clientName}`);
      onSmsSent?.();
      onOpenChange(false);
    } catch (err) {
      const error = err as Error;
      toast.error(error.message || 'Failed to send SMS');
    } finally {
      setIsSending(false);
    }
  }, [
    messageMode,
    hasEditedTemplate,
    selectedTemplate,
    renderedMessage,
    matterId,
    taskId,
    clientName,
    onSmsSent,
    onOpenChange,
    sendSms,
  ]);

  const handleCloseModal = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <>
      <Dialog open={open} onOpenChange={handleCloseModal}>
        <DialogContent
          className="max-w-4xl max-h-[85vh] flex flex-col overflow-hidden"
          zIndex={Z_INDEX_MODAL_HIGH}
        >
          <DialogHeader>
            <DialogTitle>Send Treatment SMS</DialogTitle>
          </DialogHeader>

          <div className="flex-1 flex gap-4 overflow-hidden py-4">
            {/* Left side: Conversation History */}
            <div className="w-1/3 flex flex-col min-w-0">
              <ConversationHistory
                messages={recentMessages}
                className="flex-1 overflow-hidden"
              />
            </div>

            {/* Right side: Compose Message */}
            <div className="w-2/3 flex flex-col space-y-4 overflow-y-auto">
              {/* Recipient info */}
              <div className="rounded-lg border bg-zinc-50 p-3 space-y-1">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-zinc-500">To:</span>
                  <span className="font-medium text-zinc-900">
                    {clientName}
                  </span>
                  {clientPhone && (
                    <>
                      <span className="text-zinc-400">-</span>
                      <span className="text-zinc-600">
                        {formatPhoneNumber(clientPhone)}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* No phone warning */}
              {!clientPhone && (
                <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>
                    This client does not have a phone number on file. Add a
                    phone number to send SMS.
                  </span>
                </div>
              )}

              {/* Sender not configured warning */}
              {senderConfigured === false && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>
                    Your firm&apos;s SMS sender number is not configured.
                    Contact your administrator.
                  </span>
                </div>
              )}

              {/* Category selector */}
              <div className="space-y-1.5">
                <Label className="text-xs text-zinc-500">Category</Label>
                <Select
                  value={effectiveCategory ?? undefined}
                  onValueChange={(v) =>
                    handleCategoryChange(v as SmsTemplateCategory)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Message mode toggle */}
              <div className="space-y-3">
                <RadioGroup
                  value={messageMode}
                  onValueChange={(v) =>
                    setMessageMode(v as 'template' | 'custom')
                  }
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="template" id="mode-template" />
                    <Label htmlFor="mode-template" className="cursor-pointer">
                      Use template
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="custom" id="mode-custom" />
                    <Label htmlFor="mode-custom" className="cursor-pointer">
                      Write custom message
                    </Label>
                  </div>
                </RadioGroup>

                {/* Template selector */}
                {messageMode === 'template' && (
                  <TemplateSelector
                    templates={templates}
                    selectedTemplateId={selectedTemplate?.id ?? null}
                    recommendedTemplateId={recommendedTemplate?.id ?? null}
                    onSelect={handleTemplateSelect}
                    disabled={isLoadingTemplates}
                  />
                )}

                {/* Message editor - always editable */}
                <div className="space-y-2">
                  <Label className="text-xs text-zinc-500">Message</Label>
                  <Textarea
                    value={renderedMessage}
                    onChange={(e) => handleMessageEdit(e.target.value)}
                    placeholder={
                      messageMode === 'template'
                        ? 'Select a template above...'
                        : 'Type your message...'
                    }
                    className="min-h-[140px] resize-none"
                  />
                  <div className="flex items-center justify-between text-xs text-zinc-500">
                    <span>{renderedMessage.length} characters</span>
                    {messageMode === 'template' && hasEditedTemplate && (
                      <span className="text-amber-600">
                        Template has been edited
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 gap-2">
            <Button variant="outline" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              onClick={handleSendClick}
              disabled={
                isSending ||
                !currentMessage.trim() ||
                !clientPhone ||
                !senderConfigured
              }
            >
              {isSending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send SMS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent style={{ zIndex: Z_INDEX_MODAL_NESTED }}>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm SMS</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>Are you sure you want to send this message?</p>
                <div className="rounded-lg border bg-zinc-50 p-3">
                  <p className="text-sm text-zinc-500 mb-1">To: {clientName}</p>
                  <p className="text-sm text-zinc-900 whitespace-pre-wrap">
                    {renderedMessage}
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Edit Message</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSend}>
              Send SMS
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
